import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DataPicker from "./DataPicker";
import { useDataCatalogContext } from "./data-picker/DataCatalogContext";
import { buildPickerGroups, useDataPathInfo } from "./data-picker/dataPicker";
import { useFlussFieldPath, useFlussInlineIssues } from "./node-properties/FlussNodePanelField";

export interface TemplateTextFieldProps {
    value: string;
    onChange: (value: string) => void;
    label: string;
    disabled?: boolean;
    multiline?: boolean;
    fieldPath?: string;
    focusAtEnd?: boolean;
}

const TokenPill = ({ source, index, groups, error }: {
    source: string; index: number; groups: ReturnType<typeof buildPickerGroups>; error: boolean;
}) => {
    const { t } = useTranslation();
    const info = useDataPathInfo(source, groups, t);
    const group = groups.find((candidate) => candidate.outputs.some((output) => source === output.path || source.startsWith(`${output.path}.`)));
    const label = info?.known ? (group?.kind === "record"
        ? `${t("fluss.data.picker.record_group_no_table", { defaultValue: "Registro do processo" })} › ${info.label}`
        : info.label) : source;
    return <Chip key={`${index}:${source}`} data-testid="template-token-pill" size="small"
        title={source} label={label} color={error ? "error" : "primary"} variant="outlined"
        data-error={error ? "true" : undefined} />;
};

// The editable string is canonical. Pills are a visual reading aid, never a second stored format.
export const TemplateTextField = ({ value, onChange, label, disabled = false, multiline = false, fieldPath, focusAtEnd = false }: TemplateTextFieldProps) => {
    const { t } = useTranslation();
    const catalog = useDataCatalogContext();
    const groups = buildPickerGroups(catalog, t);
    const issues = useFlussInlineIssues();
    const contextFieldPath = useFlussFieldPath();
    const issueField = fieldPath || contextFieldPath;
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    useEffect(() => {
        if (!focusAtEnd) return;
        const input = inputRef.current;
        input?.focus();
        input?.setSelectionRange(input.value.length, input.value.length);
    }, [focusAtEnd]);
    const pills = [...value.matchAll(/(?<!\\)\{\{(.*?)\}\}/g)].map((match) => match[1].trim());
    const insert = (source: string) => {
        const input = inputRef.current;
        const start = input?.selectionStart ?? value.length;
        const end = input?.selectionEnd ?? start;
        const token = `{{${source}}}`;
        onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
        requestAnimationFrame(() => {
            input?.focus();
            input?.setSelectionRange(start + token.length, start + token.length);
        });
    };

    return <Stack spacing={0.75} data-testid="template-text-field">
        <TextField
            size="small"
            fullWidth
            label={label}
            value={value}
            disabled={disabled}
            multiline={multiline}
            minRows={multiline ? 3 : undefined}
            inputRef={inputRef}
            onChange={(event) => onChange(event.target.value)}
        />
        <Typography variant="caption" color="text.secondary">
            {t("fluss.template.composition_hint", { defaultValue: "Digite o texto e use Inserir dado para incluir valores do fluxo." })}
        </Typography>
        {pills.length > 0 && <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {pills.map((source, index) => <TokenPill key={`${index}:${source}`} source={source} index={index} groups={groups}
                error={issues.some((issue) => (issue.field === issueField || issue.field === `config.${issueField}`) &&
                    Number(issue.params?.token_index) === index)} />)}
        </Box>}
        <DataPicker
            value={undefined}
            onChange={(source) => { if (typeof source === "string") insert(source); }}
            expectedType="TEMPLATE_TEXT"
            allowFixed={false}
            disabled={disabled}
            ariaLabel={String(t("fluss.template.insert_data", { defaultValue: "Insert data" }))}
        />
    </Stack>;
};

export default TemplateTextField;
